import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { LayoutService } from '../../service/layout.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { Menu } from 'primeng/menu';
import { NavigationEnd, Router } from '@angular/router';
import { SystemContextService } from '@app/core/services/system-context.service';
import { SistemaDatosService } from '@app/core/services/sistema-datos.service';
import { SystemSettingsService } from '@app/core/services/system-settings.service';
import { MobileSidebarService } from '@app/core/services/mobile-sidebar.service';
import { ModulesService, type ModuleDto, type SubmoduleDto } from '@app/core/services/modules.service';
import { Subject, catchError, filter, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { Usuario } from '@app/models/usuario';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-topbar',
    templateUrl: './app-topbar.component.html',
    styleUrls: ['./app-topbar.component.scss'],
    standalone: false
})
export class AppTopbarComponent implements OnInit, AfterViewInit, OnDestroy {
    userMenuItems: MenuItem[] = [];
    currentUser: Usuario | null = null;

    // Calculo la altura del logo preservando el aspecto (168×135 → 1.24)
    logoWidth = 56;
    logoHeight: number = Math.round(this.logoWidth * (135 / 168)); // ≈45
    menuModules: Array<{ id: string; label: string; subtext?: string | null; url?: string | null; icon?: string | null }> = [];
    menuSubmodules: Array<{ id: string; moduleId: string; label: string; url?: string | null; icon?: string | null; order?: number | null }> = [];
    menuLoading = false;
    menuError: string | null = null;
    private lastMenuSystem: string | null = null;
    private destroy$ = new Subject<void>();

    // Ítems globales (con visibilidad controlada por claims)
    private readonly allGlobalItems = [
        { key: 'usuarios', label: 'Usuarios', subtext: 'Control de Acceso', icon: 'pi pi-users', linkAbs: ['/usuarios', 'home'], claim: 'SISTEMA_USUARIOS' },
        { key: 'param', label: 'Parámetros', subtext: 'Configura el sistema', icon: 'pi pi-cog', linkAbs: ['/parametros', 'home'], claim: 'SISTEMA_PARAMETROS' }
    ];

    get globalItems() {
        const user = this.currentUser;
        const isSuperAdmin = Number((user as any)?.superadmin) === 1;
        const userClaims = new Set(user?.claims?.map((c) => c.nombre) ?? []);
        return this.allGlobalItems.filter((item) => isSuperAdmin || userClaims.has(item.claim));
    }

    get allMenuItems() {
        const modNames = this.moduleNameById;
        const subs = this.menuSubmodulesWithExtras.map((s) => ({
            id: s.id,
            label: s.label,
            subtext: modNames.get(s.moduleId) ?? '',
            icon: s.icon || 'pi pi-window-maximize',
            linkAbs: this.buildLinkFromUrl(s.url)
        }));

        const globals = this.globalItemsWithConfig.map((g) => ({
            id: g.key,
            label: g.label,
            subtext: g.subtext,
            icon: g.icon,
            linkAbs: (g as any).linkAbs || ['/']
        }));

        const extras = this.mega3.map((m, idx) => ({
            id: `extra-${idx}`,
            label: m.label,
            subtext: m.subtext,
            icon: m.icon,
            linkAbs: m.linkAbs
        }));

        const merged = [...subs, ...globals, ...extras];
        const seen = new Set<string>();
        return merged.filter((i) => {
            const key = `${i.label}-${i.subtext}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    private readonly authService = inject(AuthService);

    @ViewChild('userMenuMobile') userMenuMobile!: Menu;
    @ViewChild('popover') notificationsPopover?: Popover;
    @ViewChild('userPopover') userPopover?: Popover;

    // <-- referencia al root del topbar para medir su alto real
    @ViewChild('topbarRoot', { static: true }) topbarRoot!: ElementRef<HTMLElement>;
    private ro?: ResizeObserver;
    private alignQueued = false;

    constructor(
        public layoutService: LayoutService,
        private eRef: ElementRef,
        private router: Router,
        private systemContext: SystemContextService,
        private mobileSidebar: MobileSidebarService,
        private cdRef: ChangeDetectorRef,
        private sistemaSvc: SistemaDatosService,
        private settingsSvc: SystemSettingsService,
        private modulesSvc: ModulesService
    ) {}

    showMegaMenu = false;
    closeTimer: any;
    megaMenuVisible = false;

    openMegaMenu() {
        clearTimeout(this.closeTimer);
        if (!this.megaMenuVisible) {
            // difiere al próximo tick para evitar NG0100
            setTimeout(() => {
                this.megaMenuVisible = true;
                this.cdRef.detectChanges();
                setTimeout(() => (this.showMegaMenu = true), 10);
            }, 0);
        }
    }

    startCloseTimer() {
        this.showMegaMenu = false;
        this.closeTimer = setTimeout(() => (this.megaMenuVisible = false), 300); // match anim
    }

    cancelCloseTimer() {
        clearTimeout(this.closeTimer);
        this.showMegaMenu = true;
    }

    toggleMegaMenu() {
        // difiere al próximo tick para evitar NG0100
        setTimeout(() => {
            this.megaMenuVisible = !this.megaMenuVisible;
            this.cdRef.detectChanges();
        }, 0);
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (this.megaMenuVisible && !this.eRef.nativeElement.contains(target)) {
            // diferir cierre para evitar cambios durante el mismo ciclo
            setTimeout(() => {
                this.megaMenuVisible = false;
                this.cdRef.detectChanges();
            }, 0);
        }
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.queuePopoverAlign();
    }

    get currentSystem(): string {
        return this.systemContext.getSystem() || 'hse'; // fallback si aún no se definió
    }

    // Reemplazamos "Contacto" por "Calendario" (válido según lo nuevo del segundo archivo)
    readonly mega3 = [
        { label: 'Calendario', subtext: 'Actividades y Eventos', icon: 'pi pi-calendar', linkAbs: ['/calendario', 'home'] },
        { label: 'FAQ', subtext: 'Preguntas Frecuentes', icon: 'pi pi-question-circle', linkAbs: ['/faq'] }
    ];

    ngOnInit(): void {
        // Cargar datos para título/subtítulo/pais
        this.sistemaSvc.get().subscribe({
            next: (d) => {
                if (d) {
                    this.sdTitle = (d.titulo ?? d.nombre ?? 'Blister SIGD').toString();
                    this.sdSubtitle = (d.subtitulo ?? 'Blister Sistema Integral de Gestión Digital').toString();
                    this.sdPais = (d.pais ?? 'Argentina').toString();
                    // Iconos topbar desde BD
                    this.sdTopbarLight = d.logo_topbar || undefined;
                    this.sdTopbarDark = d.logo_topbar_dark || undefined;
                    this.cacheBuster = String(Date.now());
                    this.cdRef.markForCheck();
                }
            },
            error: () => {}
        });
        // fallback desde settings locales si BD no tuviera datos
        const settings = (window as any)?.localStorage ? null : null;
        try {
            const raw = localStorage.getItem('system_settings_administracion-blister');
            if (raw) {
                const obj = JSON.parse(raw);
                this.sdTitle = obj.systemTitle || this.sdTitle;
                this.sdSubtitle = obj.systemSubtitle || this.sdSubtitle;
                this.sdPais = obj.country || this.sdPais;
                this.sdTopbarLight = obj.topbarIconUrlLight || this.sdTopbarLight;
                this.sdTopbarDark = obj.topbarIconUrlDark || this.sdTopbarDark;
            }
        } catch {}

        this.userMenuItems = [
            {
                styleClass: 'subMenu',
                label: 'Mi Perfil',
                icon: 'pi pi-user',
                command: () => this.router.navigate(['/perfil/gestionar-contrasena'])
            },
            {
                styleClass: 'subMenu',
                label: 'Cambiar Sistema',
                icon: 'pi pi-sync',
                command: () => this.router.navigate(['/selector'])
            },
            {
                styleClass: 'subMenu',
                label: 'Cerrar sesión',
                icon: 'pi pi-sign-out',
                command: () => this.logout()
            }
        ];

        this.authService.currentUser$
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => {
                this.currentUser = user;
                this.computeAvatarUrl(user?.imagen_perfil);
            });

        // El avatar puede no estar en localStorage (cache viejo).
        // Obtenemos datos frescos de la API, igual que welcome-card.
        this.authService.returnUserInfo().then((fresh) => {
            if (fresh?.imagen_perfil) {
                this.computeAvatarUrl(fresh.imagen_perfil);
            }
        });

        // ---- medir top + alto y publicar --topbar-offset
        // al inicializar
        setTimeout(() => this.updateTopbarOffset(), 0);

        // cuando cambie el tamaño del topbar (contenido/tema)
        this.ro = new ResizeObserver(() => this.updateTopbarOffset());
        this.ro.observe(this.topbarRoot.nativeElement);

        // y ante cambios de viewport/orientación
        window.addEventListener('resize', this.updateTopbarOffset, { passive: true });
        window.addEventListener('orientationchange', this.updateTopbarOffset, { passive: true });

        //! Para notificaciones
        this.calcularNoLeidas();

        // Menú dinámico basado en módulos/submódulos
        this.loadMenuForSystem(true);

        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.loadMenuForSystem());
    }

    ngAfterViewInit(): void {
        // Primer cálculo tras pintar el DOM para evitar offset incorrecto al primer inicio/login
        setTimeout(() => this.updateTopbarOffset(), 0);
        // Recalcular cuando el visualViewport cambia (iOS PWA ajusta barras en fases)
        const vv = (window as any).visualViewport;
        if (vv) {
            vv.addEventListener('resize', this.updateTopbarOffset as any, { passive: true } as any);
        }
        // Varias pasadas escalonadas para cubrir fases del WebKit
        [80, 180, 360].forEach((ms) => setTimeout(() => this.updateTopbarOffset(), ms));
    }

    ngOnDestroy(): void {
        this.ro?.disconnect();
        window.removeEventListener('resize', this.updateTopbarOffset as any);
        window.removeEventListener('orientationchange', this.updateTopbarOffset as any);
        this.destroy$.next();
        this.destroy$.complete();
    }

    logout(): void {
        this.authService.logout();
    }

    getUserInitials(): string {
        const nombre = (this.currentUser as any)?.nombre ?? '';
        const apellido = (this.currentUser as any)?.apellido ?? '';
        const usuario = this.currentUser?.usuario ?? '';
        const initials = `${nombre.charAt(0)}${apellido.charAt(0)}`.trim();
        return initials || usuario.charAt(0).toUpperCase();
    }

    topbarAvatarUrl: string | null = null;

    private computeAvatarUrl(imagenPerfil?: string | null): void {
        const img = (imagenPerfil ?? '').trim();
        if (!img) {
            this.topbarAvatarUrl = null;
            return;
        }
        if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('assets/')) {
            this.topbarAvatarUrl = img;
        } else {
            this.topbarAvatarUrl = `${environment.imagesUrl}avatar/${img}`;
        }
    }

    onAvatarError(): void {
        this.topbarAvatarUrl = null;
    }

    getUserDisplayName(): string {
        const nombre = (this.currentUser as any)?.nombre ?? '';
        const apellido = (this.currentUser as any)?.apellido ?? '';
        const full = `${nombre} ${apellido}`.trim();
        return full || this.currentUser?.usuario || 'Usuario';
    }

    getUserUsername(): string {
        return this.currentUser?.usuario ? `@${this.currentUser.usuario}` : '';
    }

    getUserDescription(): string {
        const anyUser = this.currentUser as any;
        const descripcion =
            anyUser?.descripcion?.descripcion ??
            anyUser?.descripcionId?.descripcion ??
            anyUser?.descripcionNombre ??
            anyUser?.descripcion ??
            '';

        return descripcion || '';
    }

    onUserMenuAction(item: MenuItem): void {
        if (item.command) {
            item.command({} as any);
            return;
        }

        if (item.routerLink) {
            this.router.navigate(item.routerLink as any);
        }
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({
            ...state,
            darkTheme: !state.darkTheme
        }));
        // por si cambió la altura por estilos
        setTimeout(() => this.updateTopbarOffset(), 0);
    }

    toggleMobileMenu(event?: Event) {
        event?.stopPropagation();
        event?.preventDefault();
        this.mobileSidebar.toggle();
    }

    // ---- calcula top + alto y lo publica en --topbar-offset
    private updateTopbarOffset = () => {
        const el = this.topbarRoot?.nativeElement;
        if (!el) return;

        const cs = getComputedStyle(el);
        const top = parseFloat(cs.top || '0'); // incluye safe-area + 1rem
        const h = el.offsetHeight; // alto real del topbar
        const margin = 8; // respiro opcional
        const off = Math.max(0, Math.ceil(top + h + margin));

        document.documentElement.style.setProperty('--topbar-offset', `${off}px`);
    };

    private queuePopoverAlign(): void {
        if (this.alignQueued) return;
        this.alignQueued = true;
        requestAnimationFrame(() => {
            this.alignQueued = false;
            this.alignOpenPopover(this.notificationsPopover);
            this.alignOpenPopover(this.userPopover);
        });
    }

    private alignOpenPopover(popover?: Popover): void {
        if (!popover || !popover.overlayVisible) return;
        popover.align();
    }

    // ! NOTIFICACIONES ----------------------
    // Campos para sistema-datos (título/subtítulo/pais)
    sdTitle: string = 'Blister SIGD';
    sdSubtitle: string = 'Blister Sistema Integral de Gestión Digital';
    sdPais: string = 'Argentina';
    // Iconos del topbar (claro/oscuro)
    sdTopbarLight?: string;
    sdTopbarDark?: string;
    private cacheBuster?: string;

    get fullSubtitle(): string {
        const parts = [this.sdSubtitle, this.sdPais].filter((p) => !!p && p.trim().length > 0);
        return parts.length ? `(${parts.join(' ')})` : '';
    }

    private withV(url?: string): string | undefined {
        if (!url) return undefined;
        if (url.startsWith('assets/') || url.startsWith('data:')) return url;
        const v = this.cacheBuster || '1';
        return url.includes('?') ? `${url}&v=${v}` : `${url}?v=${v}`;
    }

    // ------------------ MENÚ DINÁMICO ---------------------------------
    private loadMenuForSystem(force: boolean = false): void {
        const system = this.currentSystem;
        if (!force && this.lastMenuSystem === system && this.menuModules.length) return;
        this.lastMenuSystem = system;
        this.menuLoading = true;
        this.menuError = null;

        this.modulesSvc
            .getModules()
            .pipe(
                map((mods) => this.filterModulesBySystem(mods || [], system)),
                switchMap((mods) => {
                    // Módulos que cargamos (incluye raíz) y los que mostramos (sin raíz)
                    const displayMods = mods.filter((m) => !this.isRootModule(m, system));
                    this.menuModules = displayMods.map((m) => ({
                        id: m.id,
                        label: m.nombre_modulo || 'Módulo',
                        subtext: m.subtitulo || '',
                        url: m.url || '',
                        icon: 'pi pi-briefcase'
                    }));

                    const loaders = mods.map((m) =>
                        this.modulesSvc.getSubmodules(m.id).pipe(
                            map((subs) =>
                                (subs || [])
                                    .filter((s) => s.activo === 1)
                                    .map((s) => ({ ...s, moduleId: m.id }))
                            ),
                            catchError(() => of<SubmoduleDto[]>([]))
                        )
                    );

                    if (!loaders.length) return of<SubmoduleDto[]>([]);

                    return forkJoin(loaders).pipe(map((lists) => lists.flat()));
                }),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (subs: any[]) => {
                    this.menuSubmodules = (subs || []).map((s) => ({
                        id: s.id,
                        moduleId: s.moduleId ?? (s as any).id_modulo,
                        label: s.nombre || 'Submódulo',
                        url: s.url || '',
                        icon: this.getSubmoduleIcon(s.url),
                        order: s.id_visible ?? null
                    }));
                    this.menuLoading = false;
                },
                error: () => {
                    this.menuModules = [];
                    this.menuSubmodules = [];
                    this.menuLoading = false;
                    this.menuError = 'No se pudo cargar el menú dinámico';
                }
            });
    }

    private filterModulesBySystem(mods: ModuleDto[], system: string): ModuleDto[] {
        const norm = (s?: string | null) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const sysNorm = norm(system);
        return mods
            .filter((m) => {
                const url = (m.url || '').replace(/^\/+/, '').toLowerCase();
                if (url === sysNorm || url.startsWith(`${system.toLowerCase()}/`)) return true;
                const name = norm(m.nombre_modulo);
                return name.includes(sysNorm);
            })
            .sort((a, b) => (a.id_visible ?? 999) - (b.id_visible ?? 999));
    }

    private isRootModule(m: ModuleDto, system: string): boolean {
        const url = (m.url || '').replace(/^\/+/, '').toLowerCase();
        const name = (m.nombre_modulo || '').toLowerCase();
        const sys = system.toLowerCase();
        return url === sys || name === sys;
    }

    // Mapa de URL de submódulo → icono (coincide con el sidebar mobile)
    private readonly submoduleIconMap: Record<string, string> = {
        gestionvehicular: 'pi pi-car',
        tarjetasobs: 'pi pi-clipboard',
        listasverificacion: 'pi pi-list-check',
        'juegos-hse': 'pi pi-objects-column',
        solicitudes: 'pi pi-calendar-clock'
    };

    // Claims requeridos por submódulo (vacío = libre para todos)
    private readonly submoduleClaimRequirements: Record<string, string[]> = {
        listasverificacion: [
            'HSE_LISTAS_VERIFICACION_CARGAR',
            'HSE_LISTAS_VERIFICACION_EDITAR',
            'HSE_LISTAS_VERIFICACION_ELIMINAR',
            'HSE_LISTAS_VERIFICACION_APROBAR',
            'HSE_LISTAS_VERIFICACION_ADMIN'
        ],
        'juegos-hse': [
            'HSE_JUEGOS',
            'HSE_JUEGOS_PODIO',
            'HSE_JUEGOS_ADMIN'
        ]
    };

    private userHasClaimsForSubmodule(url: string | null | undefined): boolean {
        const slug = (url || '').replace(/^\/+/, '').split('/')[0].toLowerCase();
        const required = this.submoduleClaimRequirements[slug];
        if (!required?.length) return true;
        const user = this.currentUser;
        if (Number((user as any)?.superadmin) === 1) return true;
        const userClaims = new Set(user?.claims?.map((c) => c.nombre) ?? []);
        return required.some((c) => userClaims.has(c));
    }

    private getSubmoduleIcon(url?: string | null): string {
        const slug = (url || '').replace(/^\/*/, '').split('/')[0].toLowerCase();
        return this.submoduleIconMap[slug] ?? 'pi pi-window-maximize';
    }

    // Mapa de ID de módulo → nombre corto (para subtext en mega menú)
    private get moduleNameById(): Map<string, string> {
        return new Map(this.menuModules.map((m) => [m.id, m.label]));
    }

    get menuSubmodulesWithExtras() {
        // Sólo submódulos reales; Configuración manual se agrega en globalItemsWithConfig para evitar duplicados
        // Filtrar por claims del usuario logueado
        return [...this.menuSubmodules]
            .filter((s) => this.userHasClaimsForSubmodule(s.url))
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }

    buildLinkFromUrl(url?: string | null): any[] | string {
        const raw = (url || '').trim();
        if (!raw) return ['/'];
        if (/^https?:\/\//i.test(raw)) return raw;
        // Siempre tratar como relativo al sistema actual (strip leading slashes)
        const cleaned = raw.replace(/^\/+/, '');
        if (!cleaned) return ['/'];
        if (cleaned.startsWith(`${this.currentSystem}/`)) return `/${cleaned}`;
        const parts = cleaned.split('/').filter(Boolean);
        // Si la URL es solo el módulo raíz (sin sub-ruta), forzar /home
        if (parts.length === 1) parts.push('home');
        return ['/', this.currentSystem, ...parts];
    }

    get globalItemsWithConfig() {
        // Solo agrega Configuración manual en admin-blister
        if (this.currentSystem === 'administracion-blister') {
            const already = this.globalItems.find((g) => g.key === 'ab-config');
            if (already) return this.globalItems;
            return [
                ...this.globalItems,
                { key: 'ab-config', label: 'Configuración', subtext: 'Panel del sistema', icon: 'pi pi-sliders-h', linkAbs: ['/administracion-blister/configuracion'], claim: '' }
            ];
        }
        return this.globalItems;
    }

    get topbarLogoUrl(): string {
        const isDark = !!this.layoutService.isDarkTheme();
        const light = this.sdTopbarLight || (this.settingsSvc.get('administracion-blister' as any) as any).topbarIconUrlLight || 'assets/images/icons/Logo_Blister.png';
        const dark = this.sdTopbarDark || (this.settingsSvc.get('administracion-blister' as any) as any).topbarIconUrlDark || 'assets/images/icons/Logo_Blister_Dark.png';
        const chosen = isDark ? dark : light;
        return this.withV(chosen) || chosen;
    }
    notificaciones = [
        {
            id: 1,
            titulo: 'Nuevo mensaje',
            mensaje: 'Tienes un nuevo mensaje de Juan Pérez.',
            fecha: new Date(),
            tipo: 'info',
            leida: false
        },
        {
            id: 2,
            titulo: 'Error en servidor',
            mensaje: 'El servidor de producción está caído.',
            fecha: new Date(Date.now() - 3600000),
            tipo: 'error',
            leida: false
        },
        {
            id: 3,
            titulo: 'Tarea completada',
            mensaje: 'La tarea #456 ha sido marcada como completada.',
            fecha: new Date(Date.now() - 7200000),
            tipo: 'exito',
            leida: true
        }
    ];

    notificacionesNoLeidas = 0;

    calcularNoLeidas() {
        this.notificacionesNoLeidas = this.notificaciones.filter((n) => !n.leida).length;
    }

    get ultimasNotificaciones() {
        return [...this.notificaciones].sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
    }

    actualizarNotificacion(notif: any) {
        notif.leida = true;
        this.calcularNoLeidas();
    }

    verTodas() {
        this.router.navigate(['/notificaciones']);
    }
}
